import { formatDateOnly } from "@/lib/dateOnly";
import { HelpTip } from "@/components/HelpTip";
import { CONTAINER_STATUS, human } from "@/lib/statusLabels";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePickerField } from "@/components/DatePickerField";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Loader2, PackageCheck, Pencil, Plus } from "lucide-react";
import { TrackingPanel } from "@/components/containers/TrackingPanel";
import { ContainerFinancePanel } from "@/components/containers/ContainerFinancePanel";
import { EtaConfirmDialog } from "@/components/containers/EtaConfirmDialog";
import { ReconcileInventoryDialog } from "@/components/containers/ReconcileInventoryDialog";
import { ReconciliationHistory } from "@/components/containers/ReconciliationHistory";
import { ContainerExceptions, ReceivingReadiness } from "@/components/containers/ContainerExceptions";

const STATUS_BADGE = {
  IN_MOROCCO: "badge-neutral",
  DEPARTED: "badge-transit",
  ARRIVING: "badge-transit",
  RECEIVED: "badge-florida",
};

export default function ContainerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [container, setContainer] = useState(null);
  const [transitionOpen, setTransitionOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ display_name: "", carrier_ref: "", departure_date: "", eta_carrier: "", eta_warehouse: "", eta_warehouse_verified: false, shipping_number: "", notes: "", status: "", note: "" });
  const [transitionForm, setTransitionForm] = useState({ departure_date: "", eta_warehouse: "" });
  const [etaConfirm, setEtaConfirm] = useState(null);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [ack, setAck] = useState(false);
  const [receiveRows, setReceiveRows] = useState({});
  const [shortages, setShortages] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconKey, setReconKey] = useState(0);
  // V4.8 RC48-CNT-002/003/004: explicit Add Product Line + audited manual correction
  const [addLineOpen, setAddLineOpen] = useState(false);
  const [addLineForm, setAddLineForm] = useState({ product_id: "", variant_id: "", expected_boxes: "", reason: "" });
  const [products, setProducts] = useState([]);
  const [productVariants, setProductVariants] = useState([]);
  const [correctTarget, setCorrectTarget] = useState(null);
  const [correctForm, setCorrectForm] = useState({ expected_boxes: "", reason: "" });

  const load = async () => {
    try {
      const { data } = await api.get(`/containers/${id}`);
      setContainer(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      navigate("/admin/containers");
    }
  };

  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => { api.get("/admin/suppliers").then(({ data }) => setSuppliers(data.filter((s) => s.active))).catch(() => {}); }, []);
  const assignSupplier = async (line, supplierId) => {
    try {
      await api.patch(`/containers/${id}/lines/${line.id}/supplier`, { supplier_id: supplierId || null, reason: "Founder assignment" });
      toast.success(supplierId ? "Supplier assigned" : "Supplier cleared");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [dispatchForm, setDispatchForm] = useState({ dispatch_date: new Date().toISOString().slice(0, 10), shipment_reference: "" });

  const handleDispatch = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/containers/${id}/dispatch`, { dispatch_date: dispatchForm.dispatch_date, shipment_reference: dispatchForm.shipment_reference || null });
      toast.success("Dispatch confirmed — goods are now In Transit");
      setDispatchOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleKeep = async () => {
    try {
      await api.post(`/containers/${id}/keep-in-morocco`, { note: "Admin confirmed goods remain in Morocco" });
      toast.success("Container kept in Morocco (recorded)");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openEdit = () => {
    setEditForm({
      display_name: container.display_name || "",
      carrier_ref: container.carrier_ref || "",
      departure_date: (container.departure_date || "").slice(0, 10),
      eta_carrier: (container.eta_carrier || "").slice(0, 10),
      eta_warehouse: (container.eta_warehouse || "").slice(0, 10),
      shipping_number: container.shipping_number || "",
      notes: container.notes || "",
      status: "",
      note: "",
    });
    setEditOpen(true);
  };

  const handleEditLogistics = async (e, confirm) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = {};
      for (const k of ["display_name", "carrier_ref", "departure_date", "eta_carrier", "eta_warehouse", "notes"]) {
        const current = (container[k] || "").slice(0, 10);
        if (editForm[k] && editForm[k] !== current) payload[k] = editForm[k];
      }
      if (editForm.shipping_number !== (container.shipping_number || "")) payload.shipping_number = editForm.shipping_number;
      if (payload.eta_warehouse && editForm.eta_warehouse_verified) payload.eta_warehouse_verified = true;
      if (editForm.status && editForm.status !== container.status) {
        payload.status = editForm.status;
        payload.note = editForm.note;
      }
      if (confirm) {
        payload.confirm_discrepancy = true;
        payload.override_reason = confirm;
      }
      await api.patch(`/containers/${id}`, payload);
      toast.success(confirm ? "ETA override confirmed and saved — audited" : "Logistics updated — audited");
      setEtaConfirm(null);
      setEditOpen(false);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.requires_confirmation) {
        setEtaConfirm(detail);
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTransition = async (e) => {
    e.preventDefault();
    setSaving(true);
    const next = container.status === "IN_MOROCCO" ? "DEPARTED" : "ARRIVING";
    try {
      await api.patch(`/containers/${id}`, {
        status: next,
        departure_date: transitionForm.departure_date || undefined,
        eta_warehouse: transitionForm.eta_warehouse || undefined,
      });
      toast.success(`Container marked ${next}`);
      setTransitionOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePreReservation = async (value) => {
    try {
      await api.patch(`/containers/${id}`, { allow_pre_reservation: value });
      toast.success(value ? "Pre-reservation enabled for this container" : "Pre-reservation disabled for this container");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openAddLine = async () => {
    setAddLineForm({ product_id: "", variant_id: "", expected_boxes: "", reason: "" });
    setProductVariants([]);
    setAddLineOpen(true);
    try { const { data } = await api.get("/products"); setProducts(data); } catch { /* non-blocking */ }
  };

  const pickProduct = async (pid) => {
    setAddLineForm((f) => ({ ...f, product_id: pid, variant_id: "" }));
    try {
      const { data } = await api.get(`/products/${pid}`);
      setProductVariants(data.variants || []);
    } catch { setProductVariants([]); }
  };

  const handleAddLine = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/containers/${id}/lines`, {
        variant_id: addLineForm.variant_id,
        expected_boxes: parseInt(addLineForm.expected_boxes, 10),
        reason: addLineForm.reason.trim(),
      });
      toast.success("Product line added — audited");
      setAddLineOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLine = async (line) => {
    const reason = window.prompt(`Remove ${line.sku} (${line.expected_boxes} boxes) from this container?\nMorocco stock will be reduced accordingly. Enter a reason:`);
    if (!reason || !reason.trim()) return;
    try {
      await api.delete(`/containers/${id}/lines/${line.id}`, { data: { reason: reason.trim() } });
      toast.success("Line removed — Morocco stock reversed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const [override, setOverride] = useState(null); // {line, form, preview}
  const openOverride = (line) => {
    const rec = line.received || {};
    setOverride({ line, preview: null, reason: "", form: container.status === "RECEIVED"
      ? { boxes_received: rec.boxes_received ?? line.expected_boxes, damaged: rec.damaged ?? 0, unsellable: rec.unsellable ?? 0, quarantined: rec.quarantined ?? 0 }
      : { expected_boxes: line.expected_boxes } });
  };
  const overridePayload = (confirm) => {
    const f = override.form;
    const body = { confirm, reason: override.reason };
    if (container.status === "RECEIVED") body.received = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, parseInt(v || 0, 10)]));
    else body.expected_boxes = parseInt(f.expected_boxes || 0, 10);
    return body;
  };
  const previewOverride = async () => {
    try {
      const { data } = await api.post(`/containers/${id}/lines/${override.line.id}/override`, overridePayload(false));
      setOverride((o) => ({ ...o, preview: data.preview }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const confirmOverride = async () => {
    setSaving(true);
    try {
      await api.post(`/containers/${id}/lines/${override.line.id}/override`, overridePayload(true));
      toast.success("Founder override applied and audited");
      setOverride(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openCorrect = (line) => {
    setCorrectTarget(line);
    setCorrectForm({ expected_boxes: String(line.expected_boxes), reason: "" });
  };

  const handleCorrect = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/containers/${id}/lines/${correctTarget.id}`, {
        expected_boxes: parseInt(correctForm.expected_boxes, 10),
        reason: correctForm.reason.trim(),
      });
      toast.success("Line corrected — audited");
      setCorrectTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openReceive = () => {
    const initial = {};
    container.lines.forEach((l) => {
      initial[l.id] = { boxes_received: l.expected_boxes, damaged: 0, unsellable: 0, quarantined: 0 };
    });
    setReceiveRows(initial);
    setShortages(null);
    setAck(false);
    api.get(`/containers/${id}/receiving-readiness`).then(({ data }) => setReadiness(data)).catch(() => setReadiness(null));
    setReceiveOpen(true);
  };

  const handleReceive = async (mode) => {
    setSaving(true);
    setShortages(null);
    try {
      const payload =
        mode === "all"
          ? { mode: "all" }
          : {
              mode: "lines",
              lines: container.lines.map((l) => ({
                line_id: l.id,
                boxes_received: parseInt(receiveRows[l.id]?.boxes_received ?? l.expected_boxes, 10),
                damaged: parseInt(receiveRows[l.id]?.damaged ?? 0, 10),
                unsellable: parseInt(receiveRows[l.id]?.unsellable ?? 0, 10),
                quarantined: parseInt(receiveRows[l.id]?.quarantined ?? 0, 10),
              })),
            };
      await api.post(`/containers/${id}/receive`, { ...payload, acknowledge_exclusions: ack });
      toast.success("Container received into Florida inventory");
      setReceiveOpen(false);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.shortages) {
        setShortages(detail.shortages);
        toast.error("Shortage against reserved/sold — resolve before receiving");
      } else if (err.response?.status === 409 && ["IMPORT_EXCEPTIONS_UNRESOLVED", "EXCLUSION_ACK_REQUIRED"].includes(detail?.code)) {
        api.get(`/containers/${id}/receiving-readiness`).then(({ data }) => setReadiness(data)).catch(() => {});
        toast.error(detail.message);
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setSaving(false);
    }
  };

  if (!container) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" data-testid="container-detail-loading">
        <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
      </div>
    );
  }

  // V5.2: the generic "Mark Departed" path bypassed Morocco inventory accounting — Confirm Dispatch is authoritative.
  // Port/vessel stages live in the tracking panel; status corrections stay in Edit logistics (note required).
  const canTransition = false;
  const canReceive = container.status === "ARRIVING" || container.status === "DEPARTED";

  return (
    <div data-testid="container-detail-page">
      <Button variant="ghost" onClick={() => navigate("/admin/containers")} data-testid="back-to-containers" className="mb-6 -ml-3 gap-2 text-warmgrey hover:text-charcoal">
        <ArrowLeft className="h-4 w-4" /> All containers
      </Button>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{container.carrier_ref || "No carrier ref"}</p>
          <h1 className="mt-2 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl" data-testid="container-display-name">{container.zc_ref || container.display_name || container.container_number}</h1>
          <p className="mt-1 font-mono text-xs text-muted-foreground" data-testid="container-internal-ref">Packing list #{container.container_number} · Ocean container: <span data-testid="shipping-number-display">{container.shipping_number || "Pending"}</span></p>
          {container.supplier_summary && (
            <p className="mt-1 text-xs text-charcoal" data-testid="container-supplier-summary">{container.supplier_summary.label}</p>
          )}
        </div>
        <span className={`badge-status ${STATUS_BADGE[container.status]}`} data-testid="container-status-badge">
          {human(CONTAINER_STATUS, container.status)}
        </span>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
        {[
          { label: "Departure", value: container.departure_date ? formatDateOnly(container.departure_date) : "—" },
          { label: "Miami port ETA (carrier)", value: container.eta_carrier ? formatDateOnly(container.eta_carrier) : "—" },
          { label: container.eta_warehouse_kind === "VERIFIED_APPOINTMENT" ? "Warehouse delivery (verified appointment)" : "Warehouse ETA (provisional · port ETA + 5 days)", value: container.eta_warehouse ? formatDateOnly(container.eta_warehouse) : "—" },
          { label: "Shipping number", value: container.shipping_number || "Shipping number pending" },
          { label: "Received", value: container.received_at ? new Date(container.received_at).toLocaleString() : "—" },
        ].map((f) => (
          <div key={f.label} className="rounded-md border border-border bg-white p-5">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{f.label}</p>
            <p className="mt-2 text-sm font-medium text-charcoal">{f.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-4">
        {container.lines.length > 0 && (
          <Button variant="outline" onClick={() => setReconcileOpen(true)} data-testid="reconcile-inventory-button" className="gap-2 border-charcoal text-charcoal">
            <PackageCheck className="h-4 w-4" /> Reconcile Inventory
          </Button>
        )}
        <ReconcileInventoryDialog containerId={id} open={reconcileOpen} onOpenChange={setReconcileOpen} onDone={() => { load(); setReconKey((k) => k + 1); }} />
        {container.status === "IN_MOROCCO" && (
          <>
            <Dialog open={dispatchOpen} onOpenChange={setDispatchOpen}>
              <DialogTrigger asChild>
                <Button data-testid="confirm-dispatch-button" className="h-11 bg-terracotta text-white hover:bg-terracotta-hover">Confirm Dispatch</Button>
              </DialogTrigger>
              <DialogContent className="border-border bg-white sm:max-w-md" data-testid="dispatch-dialog">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl font-medium text-charcoal">Confirm dispatch from Morocco</DialogTitle>
                  <DialogDescription className="text-sm text-warmgrey">Only after the goods have physically left Morocco. Quantities move from Morocco to In Transit.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleDispatch} className="mt-2 space-y-4" data-testid="dispatch-form">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Dispatch date *</Label>
                    <DatePickerField required value={dispatchForm.dispatch_date} onChange={(v) => setDispatchForm((f) => ({ ...f, dispatch_date: v }))} testId="dispatch-date-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Shipment reference</Label>
                    <Input value={dispatchForm.shipment_reference} onChange={(e) => setDispatchForm((f) => ({ ...f, shipment_reference: e.target.value }))} placeholder={container.shipping_number || "Container shipping number"} data-testid="dispatch-reference-input" className="h-11 bg-white" />
                  </div>
                  <Button type="submit" disabled={saving} data-testid="dispatch-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm dispatch"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            <Button variant="outline" onClick={handleKeep} data-testid="keep-in-morocco-button" className="h-11 border-limestone text-charcoal hover:bg-sand">Keep in Morocco</Button>
          </>
        )}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="edit-logistics-button" onClick={openEdit} className="h-11 border-limestone text-charcoal hover:bg-sand">Edit logistics / status</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-medium text-charcoal">Edit logistics</DialogTitle>
              <DialogDescription className="text-sm text-warmgrey">
                Metadata edits never change status, inventory, pricing or container lines — every change is audited (old → new, who, when). Status corrections, including backward corrections, require a note. Receiving stays a protected transactional action.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditLogistics} className="mt-2 space-y-4" data-testid="edit-logistics-form">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Business display name</Label>
                <Input data-testid="edit-display-name" value={editForm.display_name} onChange={(e) => setEditForm((f) => ({ ...f, display_name: e.target.value }))} className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Carrier reference</Label>
                <Input data-testid="edit-carrier-ref" value={editForm.carrier_ref} onChange={(e) => setEditForm((f) => ({ ...f, carrier_ref: e.target.value }))} className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="space-y-2"><Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Departure</Label><DatePickerField testId="edit-departure-date" value={editForm.departure_date} onChange={(v) => setEditForm((f) => ({ ...f, departure_date: v }))} /></div>
                <div className="space-y-2"><Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Miami port ETA (carrier)</Label><DatePickerField testId="edit-eta-carrier" value={editForm.eta_carrier} onChange={(v) => setEditForm((f) => ({ ...f, eta_carrier: v }))} /></div>
                <div className="space-y-2"><Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Container shipping number</Label><Input data-testid="edit-shipping-number" value={editForm.shipping_number} onChange={(e) => setEditForm((f) => ({ ...f, shipping_number: e.target.value }))} className="h-11 bg-white focus-visible:ring-terracotta" /></div>
                <div className="space-y-2"><Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Warehouse ETA</Label><label className="flex items-center gap-2 text-xs text-charcoal"><input type="checkbox" data-testid="edit-eta-warehouse-verified" checked={editForm.eta_warehouse_verified} onChange={(e) => setEditForm((f) => ({ ...f, eta_warehouse_verified: e.target.checked }))} /> Verified delivery appointment (kept when carrier ETA changes)</label><DatePickerField testId="edit-eta-warehouse" value={editForm.eta_warehouse} onChange={(v) => setEditForm((f) => ({ ...f, eta_warehouse: v }))} /></div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Notes</Label>
                <Input data-testid="edit-notes" value={editForm.notes} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Status correction (optional)</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm((f) => ({ ...f, status: v === "none" ? "" : v }))} disabled={container.status === "RECEIVED"}>
                  <SelectTrigger data-testid="edit-status-select" className="h-11 bg-white"><SelectValue placeholder="No change" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">No change</SelectItem>
                    <SelectItem value="IN_MOROCCO">In Morocco — preparing</SelectItem>
                    <SelectItem value="DEPARTED">In transit (correction only — no inventory move; use Confirm Dispatch to depart)</SelectItem>
                    <SelectItem value="ARRIVING">Arriving Miami</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editForm.status && editForm.status !== "none" && editForm.status !== container.status && (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Correction note *</Label>
                  <Input required data-testid="edit-status-note" value={editForm.note} onChange={(e) => setEditForm((f) => ({ ...f, note: e.target.value }))} placeholder="Why is this correction needed?" className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
              )}
              <Button type="submit" disabled={saving} data-testid="edit-logistics-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save (audited)"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        <EtaConfirmDialog detail={etaConfirm} saving={saving} onCancel={() => setEtaConfirm(null)} onConfirm={(reason) => handleEditLogistics(null, reason)} />
        {container.status !== "RECEIVED" && (
          <Button variant="outline" onClick={openAddLine} data-testid="add-line-button" className="h-11 border-limestone text-charcoal hover:bg-sand">
            <Plus className="mr-2 h-4 w-4" /> Add product line
          </Button>
        )}
        {canTransition && (
          <Dialog open={transitionOpen} onOpenChange={setTransitionOpen}>
            <DialogTrigger asChild>
              <Button data-testid="transition-button" className="h-11 bg-terracotta text-white hover:bg-terracotta-hover">
                Mark {container.status === "IN_MOROCCO" ? "Departed" : "Arriving"}
              </Button>
            </DialogTrigger>
            <DialogContent className="border-border bg-white sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-medium text-charcoal">
                  Mark {container.status === "IN_MOROCCO" ? "Departed" : "Arriving"}
                </DialogTitle>
                <DialogDescription className="text-sm text-warmgrey">
                  Status changes never create inventory — only Receive does.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleTransition} className="mt-2 space-y-5" data-testid="transition-form">
                {container.status === "IN_MOROCCO" ? (
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Departure date *</Label>
                    <DatePickerField required testId="departure-date-input" value={transitionForm.departure_date} onChange={(v) => setTransitionForm((f) => ({ ...f, departure_date: v }))} />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">ETA warehouse</Label>
                    <DatePickerField testId="eta-warehouse-input" value={transitionForm.eta_warehouse} onChange={(v) => setTransitionForm((f) => ({ ...f, eta_warehouse: v }))} />
                  </div>
                )}
                <Button type="submit" disabled={saving} data-testid="transition-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
        {canReceive && container.lines.length > 0 && (
          <Button onClick={openReceive} data-testid="receive-container-button" className="h-11 bg-charcoal text-white hover:bg-charcoal/90">
            <PackageCheck className="mr-2 h-4 w-4" /> Receive Container
          </Button>
        )}
        {container.status !== "RECEIVED" && (
          <label className="flex items-center gap-3 rounded-md border border-border bg-white px-4 py-2">
            <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-warmgrey">
              Allow pre-reservation
              <HelpTip testid="help-pre-reserve" text="When enabled, dealers may reserve inventory that is still on this container (not yet received in Florida). Reserved boxes are held for the reservation and become fulfillable when the container is received. Disable to keep this container's inventory unavailable until receipt." />
            </span>
            <Switch checked={container.allow_pre_reservation} onCheckedChange={handleTogglePreReservation} data-testid="container-pre-reserve-toggle" />
          </label>
        )}
      </div>

      <TrackingPanel container={container} onChanged={load} />
      <ContainerFinancePanel cid={id} />

      <div className="mt-8 overflow-x-auto rounded-md border border-border bg-white shadow-[0_2px_12px_rgba(26,26,24,0.04)]">
        <Table data-testid="container-lines-table" className="min-w-[900px]">
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Variant</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Supplier</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest text-muted-foreground">Expected</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest text-muted-foreground">Reserved</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest text-muted-foreground">Sold</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest text-muted-foreground">Available</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Received</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest text-muted-foreground">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {container.lines.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="py-12 text-center text-sm text-muted-foreground" data-testid="lines-empty">No lines yet — import a packing list or add a product line to allocate stock.</TableCell></TableRow>
            ) : (
              container.lines.map((l) => (
                <TableRow key={l.id} className="border-border" data-testid={`line-row-${l.sku}`}>
                  <TableCell className="py-4">
                    <p className="font-medium text-charcoal" data-testid={`line-label-${l.sku}`}>{l.identity_label || l.variant_label}</p>
                    {l.recon_status && l.recon_status !== "ACTIVE" && <span className="badge-status badge-sold" data-testid={`line-recon-status-${l.sku}`}>{l.recon_status === "MERGED" ? "Merged (reconciliation)" : "Removed (reconciliation)"}</span>}
                    {l.recon_origin && <span className="badge-status badge-neutral" data-testid={`line-recon-origin-${l.sku}`}>{l.recon_origin.type === "EXCLUDED_ROW" ? `From excluded row ${l.recon_origin.row}` : l.recon_origin.type === "SPLIT" ? `Split from ${l.recon_origin.split_from_sku}` : l.recon_origin.type === "OVERAGE" ? "Overage" : "Added in reconciliation"}</span>}
                    {l.declared && l.declared.sku && (l.declared.sku !== l.sku || l.declared.boxes !== l.expected_boxes) && <p className="text-[11px] text-warmgrey" data-testid={`line-declared-${l.sku}`}>Supplier declared {l.declared.sku} · {l.declared.boxes} boxes</p>}
                  </TableCell>
                  <TableCell className="py-4">
                    <select data-testid={`line-supplier-${l.sku}`} value={l.supplier_id || ""} onChange={(e) => assignSupplier(l, e.target.value)} className="h-9 rounded-md border border-border bg-white px-2 text-xs text-charcoal">
                      <option value="">Unassigned</option>
                      {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
                    </select>
                    <p className="font-mono text-xs text-muted-foreground">{l.box_coverage_sqft_snapshot} sqft/box</p>
                  </TableCell>
                  <TableCell className="py-4 text-right text-sm text-charcoal">{l.expected_boxes}</TableCell>
                  <TableCell className="py-4 text-right text-sm text-[#8a6435]">{l.reserved_boxes}</TableCell>
                  <TableCell className="py-4 text-right text-sm text-status-sold">{l.committed_boxes}</TableCell>
                  <TableCell className="py-4 text-right">
                    <span className="badge-status badge-available" data-testid={`line-available-${l.sku}`}>
                      {l.received
                        ? Math.max(0, l.received.boxes_received - l.received.damaged - l.received.unsellable - l.reserved_boxes - l.committed_boxes)
                        : l.available_boxes}
                    </span>
                  </TableCell>
                  <TableCell className="py-4 text-xs text-muted-foreground">
                    {l.received ? `${l.received.boxes_received} rec · ${l.received.damaged} dmg · ${l.received.unsellable} unsell. · ${l.received.quarantined ?? 0} quar.${l.received.corrected ? " · corrected" : ""}` : "—"}
                  </TableCell>
                  <TableCell className="py-4 text-right">
                    {container.status === "IN_MOROCCO" ? (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => openCorrect(l)} data-testid={`correct-line-${l.sku}`} className="text-warmgrey hover:text-charcoal">
                          <Pencil className="mr-1 h-3.5 w-3.5" /> Correct
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleRemoveLine(l)} data-testid={`remove-line-${l.sku}`} className="text-warmgrey hover:text-destructive">
                          Remove
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => openOverride(l)} data-testid={`override-line-${l.sku}`} className="text-terracotta hover:text-terracotta-hover">
                        Founder override
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* RC48-CNT-004: manual line changes — actor, time, reason, before/after */}
      {container.lines.some((l) => (l.history || []).length > 0) && (
        <div className="mt-6 rounded-md border border-border bg-white p-4" data-testid="line-audit-trail">
          <p className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Line audit trail</p>
          <ul className="mt-2 space-y-1">
            {container.lines.flatMap((l) => (l.history || []).map((h, i) => (
              <li key={`${l.id}-${i}`} className="text-xs text-charcoal" data-testid={`line-audit-${l.id}-${i}`}>
                {h.action === "ADDED" ? "Added" : h.action === "FOUNDER_OVERRIDE" ? "Founder override" : h.action === "RECONCILED" ? `Reconciled #${h.version}` : h.action === "REMOVED" ? "Removed" : "Corrected"}: {l.identity_label || l.variant_label} — {h.mode === "RECEIPT_CORRECTION"
                  ? Object.keys(h.after || {}).filter((k) => (h.before || {})[k] !== h.after[k]).map((k) => `${k} ${(h.before || {})[k] ?? "—"} → ${h.after[k]}`).join(", ") || "no change"
                  : `${h.before ? `${h.before.expected_boxes} → ` : ""}${h.after?.expected_boxes} boxes`} · {h.actor} · {new Date(h.at).toLocaleString()}{h.reason ? ` — ${h.reason}` : ""}
              </li>
            )))}
          </ul>
        </div>
      )}

      <ContainerExceptions containerId={id} containerStatus={container?.status} onChanged={() => { load(); setReconKey((k) => k + 1); }} />
      <ReconciliationHistory containerId={id} refreshKey={reconKey} />

      <Dialog open={addLineOpen} onOpenChange={setAddLineOpen}>
        <DialogContent className="border-border bg-white sm:max-w-md" data-testid="add-line-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium">Add product line</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              Adds a line to this container and allocates the boxes from Morocco stock — the same rule as a packing-list import. Every addition is audited with your identity and reason.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddLine} className="mt-2 space-y-4" data-testid="add-line-form">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Product</Label>
              <Select value={addLineForm.product_id} onValueChange={pickProduct}>
                <SelectTrigger data-testid="add-line-product" className="h-11 bg-white"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Variant</Label>
              <Select value={addLineForm.variant_id} onValueChange={(v) => setAddLineForm((f) => ({ ...f, variant_id: v }))} disabled={!addLineForm.product_id}>
                <SelectTrigger data-testid="add-line-variant" className="h-11 bg-white"><SelectValue placeholder={addLineForm.product_id ? "Select variant" : "Choose a product first"} /></SelectTrigger>
                <SelectContent className="bg-white">
                  {productVariants.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.sku} · {v.format_in} · {v.presentation}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Expected boxes</Label>
              <Input type="number" min="1" required data-testid="add-line-boxes" value={addLineForm.expected_boxes} onChange={(e) => setAddLineForm((f) => ({ ...f, expected_boxes: e.target.value }))} className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Reason *</Label>
              <Input required data-testid="add-line-reason" value={addLineForm.reason} onChange={(e) => setAddLineForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Why is this line added manually?" className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <Button type="submit" disabled={saving || !addLineForm.variant_id || !addLineForm.expected_boxes || !addLineForm.reason.trim()} data-testid="add-line-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add line (audited)"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!override} onOpenChange={(o) => !o && setOverride(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-lg" data-testid="override-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Founder override · {override?.line.sku}</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              This container is {container.status === "RECEIVED" ? "already received" : "in transit"}. Corrections are recorded as auditable adjustments; the original movements are never rewritten.
            </DialogDescription>
          </DialogHeader>
          {override && (
            <div className="mt-2 space-y-4" data-testid="override-form">
              <div className="grid grid-cols-2 gap-3">
                {Object.keys(override.form).map((k) => (
                  <div className="space-y-1" key={k}>
                    <Label className="text-[10px] font-semibold uppercase tracking-widest text-warmgrey">{k.replace("_", " ")}</Label>
                    <Input type="number" min="0" data-testid={`override-${k}-input`} value={override.form[k]} onChange={(e) => setOverride((o) => ({ ...o, preview: null, form: { ...o.form, [k]: e.target.value } }))} className="h-10 bg-white focus-visible:ring-terracotta" />
                  </div>
                ))}
              </div>
              {!override.preview ? (
                <Button onClick={previewOverride} variant="outline" data-testid="override-preview-button" className="h-11 w-full border-terracotta text-terracotta hover:bg-terracotta/5">Review consequences</Button>
              ) : (
                <div className="space-y-3 rounded-md border border-terracotta/40 bg-terracotta/5 p-4" data-testid="override-preview">
                  <p className="text-xs font-semibold uppercase tracking-widest text-terracotta">Warning — review before confirming</p>
                  <p className="text-xs text-charcoal"><strong>Current:</strong> {JSON.stringify(override.preview.current)}</p>
                  <p className="text-xs text-charcoal"><strong>Proposed:</strong> {JSON.stringify(override.preview.proposed)}</p>
                  <ul className="list-disc space-y-1 pl-4 text-xs text-charcoal">
                    {override.preview.consequences.map((c, i) => <li key={i} data-testid="override-consequence">{c}</li>)}
                  </ul>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-semibold uppercase tracking-widest text-warmgrey">Reason (required)</Label>
                    <Input data-testid="override-reason" value={override.reason} onChange={(e) => setOverride((o) => ({ ...o, reason: e.target.value }))} placeholder="Why is this correction needed?" className="h-10 bg-white focus-visible:ring-terracotta" />
                  </div>
                  <Button onClick={confirmOverride} disabled={saving || !override.reason.trim()} data-testid="override-confirm" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Founder override"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!correctTarget} onOpenChange={(o) => !o && setCorrectTarget(null)}>
        <DialogContent className="border-border bg-white sm:max-w-md" data-testid="correct-line-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium">Correct line — {correctTarget?.variant_label}</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              Manual corrections adjust the Morocco allocation by the difference and are audited with before/after, your identity, time and reason.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCorrect} className="mt-2 space-y-4" data-testid="correct-line-form">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Expected boxes (was {correctTarget?.expected_boxes})</Label>
              <Input type="number" min="1" required data-testid="correct-line-boxes" value={correctForm.expected_boxes} onChange={(e) => setCorrectForm((f) => ({ ...f, expected_boxes: e.target.value }))} className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Reason *</Label>
              <Input required data-testid="correct-line-reason" value={correctForm.reason} onChange={(e) => setCorrectForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Why is this correction needed?" className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <Button type="submit" disabled={saving || !correctForm.reason.trim()} data-testid="correct-line-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save correction (audited)"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={receiveOpen} onOpenChange={setReceiveOpen}>
        <DialogContent className="max-h-[90vh] w-[calc(100vw-1.5rem)] grid-cols-[minmax(0,1fr)] max-w-3xl overflow-y-auto border-border bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Receive {container.zc_ref || container.container_number}</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              One transactional operation. Reserved and sold quantities carry into Florida. Shortages against commitments block the receive.
            </DialogDescription>
          </DialogHeader>
          <ReceivingReadiness r={readiness} ack={ack} setAck={setAck} onReview={() => { setReceiveOpen(false); document.getElementById("import-exceptions")?.scrollIntoView({ behavior: "smooth" }); }} />

          {shortages && (
            <div className="rounded-md border border-status-sold/40 bg-[#8C3A3A]/5 p-4" data-testid="shortage-panel">
              <p className="text-sm font-semibold text-status-sold">Receiving blocked — shortage against commitments:</p>
              <ul className="mt-2 space-y-1 text-sm text-charcoal">
                {shortages.map((s) => (
                  <li key={s.line_id} data-testid={`shortage-${s.line_id}`}>
                    {s.variant_label}: received sellable {s.sellable_received} vs reserved {s.reserved_boxes} + sold {s.committed_boxes} — short by {s.shortfall} boxes. Reduce the reservation/order or correct the counts, then retry.
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-4 space-y-4">
            {container.lines.map((l) => (
              <div key={l.id} className="grid grid-cols-1 items-end gap-4 rounded-md border border-border bg-background p-4 sm:grid-cols-2 lg:grid-cols-5" data-testid={`receive-line-${l.sku}`}>
                <div>
                  <p className="text-sm font-medium text-charcoal">{l.variant_label}</p>
                  <p className="text-xs text-muted-foreground">Expected {l.expected_boxes} · Reserved {l.reserved_boxes} · Sold {l.committed_boxes}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-semibold uppercase tracking-widest text-warmgrey">Received</Label>
                  <Input type="number" min="0" data-testid={`receive-input-${l.sku}`} value={receiveRows[l.id]?.boxes_received ?? l.expected_boxes} onChange={(e) => setReceiveRows((r) => ({ ...r, [l.id]: { ...r[l.id], boxes_received: e.target.value } }))} className="h-10 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-semibold uppercase tracking-widest text-warmgrey">Damaged</Label>
                  <Input type="number" min="0" data-testid={`damaged-input-${l.sku}`} value={receiveRows[l.id]?.damaged ?? 0} onChange={(e) => setReceiveRows((r) => ({ ...r, [l.id]: { ...r[l.id], damaged: e.target.value } }))} className="h-10 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-semibold uppercase tracking-widest text-warmgrey">Unsellable</Label>
                  <Input type="number" min="0" data-testid={`unsellable-input-${l.sku}`} value={receiveRows[l.id]?.unsellable ?? 0} onChange={(e) => setReceiveRows((r) => ({ ...r, [l.id]: { ...r[l.id], unsellable: e.target.value } }))} className="h-10 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-semibold uppercase tracking-widest text-warmgrey">Quarantined</Label>
                  <Input type="number" min="0" data-testid={`quarantined-input-${l.sku}`} value={receiveRows[l.id]?.quarantined ?? 0} onChange={(e) => setReceiveRows((r) => ({ ...r, [l.id]: { ...r[l.id], quarantined: e.target.value } }))} className="h-10 bg-white focus-visible:ring-terracotta" />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button onClick={() => handleReceive("all")} disabled={saving || readiness?.state === "BLOCKED" || (readiness?.state === "ACK_REQUIRED" && !ack)} variant="outline" data-testid="receive-all-button" className="h-11 flex-1 border-limestone text-charcoal hover:bg-sand">
              Receive All (actual = expected)
            </Button>
            <Button onClick={() => handleReceive("lines")} disabled={saving || readiness?.state === "BLOCKED" || (readiness?.state === "ACK_REQUIRED" && !ack)} data-testid="receive-lines-button" className="h-11 flex-1 bg-terracotta text-white hover:bg-terracotta-hover">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Receive with entered counts"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
